<script lang="ts">
    import CN from "classnames";
    import { onMount } from "svelte";

    import Button from "./Buttons/Button.svelte";

    export let text: string;
    export let height = "5em";
    export let gap = "0.1em";

    let className: string | undefined = undefined;
    export { className as class };

    let expanded = false;
    let isOverflowing = false;
    let maxHeight = "1000px";
    let textContainer: HTMLDivElement;

    const toggle = () => {
        expanded = !expanded;
    };

    const checkOverflow = () => {
        if (!textContainer) return;

        maxHeight = `${textContainer.scrollHeight}px`;
        isOverflowing = textContainer.scrollHeight > textContainer.clientHeight;
    };

    onMount(() => {
        let observer: ResizeObserver;

        if (textContainer) {
            observer = new ResizeObserver(() => {
                checkOverflow();
            });

            observer.observe(textContainer);
            checkOverflow();
        }

        return () => observer?.disconnect();
    });

    const onTextChange = () => {
        expanded = false;
        checkOverflow();
    };

    $: {
        text;
        onTextChange();
    }

    /**
     * Удаляет пробелы, табы и переносы строк в конце текста.
     *
     * Это необходимо при использовании `white-space: break-spaces`,
     * так как:
     * - пробелы и табы сохраняются и занимают место;
     * - `\n` создаёт новую строку;
     *
     * Если в конце текста остались пробелы или `\n`, они будут видны
     * как лишняя пустая строка или отступ внизу.
     */
    const cleanTextEnd = (text: string) => {
        return text.replace(/\s+$/g, "");
    };
</script>

<div class={CN("gw-view-more", className)} style="--gap: {gap};">
    <div
        bind:this={textContainer}
        class="gw-view-more__container"
        class:gw-view-more__masked={!expanded && isOverflowing}
        style="--height: {height}; --maxHeight: {expanded
            ? maxHeight
            : height};"
    >
        <p class="gw-view-more__text">{cleanTextEnd(text)}</p>
    </div>

    {#if isOverflowing || expanded}
        <Button variant="link" class="gw-view-more__button" on:click={toggle}>
            {#if expanded}
                <slot name="view_less_text" />
            {:else}
                <slot name="view_more_text" />
            {/if}
        </Button>
    {/if}
</div>

<style lang="postcss">
    .gw-view-more {
        position: relative;

        &__container {
            overflow: hidden;
            position: relative;
            transition: max-height 0.3s ease;
            max-height: var(--maxHeight);
        }

        &__masked {
            mask-image: linear-gradient(to bottom, black 70%, transparent 100%);
            -webkit-mask-image: linear-gradient(
                to bottom,
                black 70%,
                transparent 100%
            );
            mask-size: 100% 100%;
            mask-repeat: no-repeat;
            -webkit-mask-size: 100% 100%;
            -webkit-mask-repeat: no-repeat;
        }

        &__text {
            margin: 0;
            padding: 0;
            line-height: 1.5;
            white-space: break-spaces;
        }
    }

    :global(.gw-btn.gw-button.gw-view-more__button) {
        margin: var(--gap, 0.1em) 0 0;
        font-size: 1em;
        text-transform: capitalize;
    }
</style>
